import React, { useEffect, useMemo, useState } from 'react'
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Tabs,
  List,
  Avatar,
  Tag,
  Space,
  Modal,
  message,
  Badge,
  Typography,
  Spin,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select
} from 'antd'
import {
  LogoutOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  CalculatorOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  EyeOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  PrinterOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useVehicleStore } from '../stores/vehicleStore'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import { app, ensureLogin } from '../services/tcbClient'
import { formatPeriodLabel, getOutstandingAtDate, getVehicleDueDate, hasExplicitChargeRange, isLeaseStarted, getAdjustmentRevenueDelta, getExpectedBetween, getActiveLeaseStart, isReceivableVehicleStatus } from './vehicleManagerUtils'

const { Text, Title } = Typography

type VoucherLine = {
  summary: string
  subject: string
  debit?: number
  credit?: number
}

type VoucherItem = {
  plate?: string
  amount?: number
  company?: string
  dueDate?: string
  periodKey?: string
  chargeRangeStart?: string
  chargeRangeEnd?: string
}

type VoucherData = {
  voucherNo: string
  date: string
  pageNo?: string
  totalPages?: string
  attachmentCount?: number
  accountant?: string
  bookkeeper?: string
  maker?: string
  reviewer?: string
  cashier?: string
  printDate?: string
  lines: VoucherLine[]
  totalDebit?: number
  totalCredit?: number
  scope?: 'batch' | 'single'
  items?: VoucherItem[]
  accountantId?: string
  accountantName?: string
  accountantSealUrl?: string
  accountantSealPreviewUrl?: string
}

const defaultLine = (): VoucherLine => ({
  summary: '',
  subject: '',
  debit: undefined,
  credit: undefined
})

const AccountantPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('pending')
  const [loading, setLoading] = useState(true)
  const [pendingReceipts, setPendingReceipts] = useState<any[]>([])
  const [completedReceipts, setCompletedReceipts] = useState<any[]>([])
  const [statistics, setStatistics] = useState({
    pendingAccounting: 0,
    completedToday: 0,
    totalAmount: 0
  })
  const [voucherModalVisible, setVoucherModalVisible] = useState(false)
  const [voucherPreviewVisible, setVoucherPreviewVisible] = useState(false)
  const [voucherForm] = Form.useForm()
  const watchedItems = Form.useWatch('items', voucherForm) as VoucherItem[] | undefined
  const [editingReceipt, setEditingReceipt] = useState<any>(null)
  const [previewData, setPreviewData] = useState<VoucherData | null>(null)
  const [allReceipts, setAllReceipts] = useState<any[]>([])
  const [adjustments, setAdjustments] = useState<any[]>([])
  const [expiringModalVisible, setExpiringModalVisible] = useState(false)
  const [rentedExpanded, setRentedExpanded] = useState(false)
  const [receivableExportVisible, setReceivableExportVisible] = useState(false)
  const [receivableExportForm] = Form.useForm()
  const [receivedExportVisible, setReceivedExportVisible] = useState(false)
  const [receivedExportForm] = Form.useForm()
  const [summaryExportVisible, setSummaryExportVisible] = useState(false)
  const [summaryExportForm] = Form.useForm()

  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { vehicles, fetchVehicles } = useVehicleStore()

  useEffect(() => {
    loadData()
  }, [])

  const formatPlate = (plate: any) => {
    if (plate === null || plate === undefined) return ''
    if (typeof plate === 'number') return Number.isFinite(plate) ? plate.toFixed(0) : ''
    return String(plate)
  }

  const resolveCloudFileUrl = async (rawUrl?: string) => {
    if (!rawUrl) return ''
    if (!String(rawUrl).startsWith('cloud://')) return String(rawUrl)
    try {
      await ensureLogin()
      const storage = (app as any).storage?.()
      const useStorage = Boolean(storage?.getTempFileURL)
      const fileList = [{ fileID: rawUrl, maxAge: 24 * 60 * 60 }]
      const res: any = useStorage
        ? await storage.getTempFileURL({ fileList })
        : await (app as any).getTempFileURL({ fileList })
      return (
        res?.fileList?.[0]?.tempFileURL ||
        res?.fileList?.[0]?.tempFileUrl ||
        res?.fileList?.[0]?.download_url ||
        ''
      )
    } catch {
      return ''
    }
  }

  const resolveAccountantSealUrl = async (source?: any, options?: { allowCurrentUser?: boolean }) => {
    const directUrl = String(
      source?.accountantSealUrl ||
      source?.voucherData?.accountantSealUrl ||
      ''
    ).trim()
    if (directUrl) return directUrl

    const identities = [
      source?.accountantId,
      source?.voucherData?.accountantId,
      source?.accountantName,
      source?.voucherData?.accountantName,
      source?.accountant,
      source?.voucherData?.accountant,
      source?.bookkeeper,
      source?.voucherData?.bookkeeper
    ]
      .map(v => String(v || '').trim())
      .filter(Boolean)

    if (options?.allowCurrentUser) {
      identities.push(
        String(user?.id || '').trim(),
        String(user?.username || '').trim(),
        String(user?.nickName || '').trim(),
        String(user?.nickname || '').trim()
      )
    }

    for (const identity of identities) {
      const res = await simpleDirectCloudApi.getUserByIdentity(identity)
      if (res.success && res.data?.makerSealUrl) {
        return String(res.data.makerSealUrl)
      }
    }

    return ''
  }

  const getReceiptItems = (receipt: any) => {
    const raw = receipt?.items
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object') return Object.values(raw)
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') return Object.values(parsed)
      } catch {
        return []
      }
    }
    return []
  }

  const normalizeReceipts = (list: any[]) =>
    list.map(r => {
      const items = getReceiptItems(r)
      const total = items.reduce(
        (s: number, it: any) => s + (Number(it.actualAmount ?? it.receivedAmount ?? it.expectedAmount ?? it.amount ?? 0) || 0),
        0
      )
      const firstItem = items[0] || {}
      const firstExplicitRange = items.find((it: any) => hasExplicitChargeRange(it, r))
      const chargeRangeSource = firstExplicitRange || (hasExplicitChargeRange(undefined, r) ? r : null)
      const lastRangeEnd = items
        .filter((it: any) => hasExplicitChargeRange(it, r))
        .map((it: any) => String(it.chargeRangeEnd || r.chargeRangeEnd || '').trim())
        .find(Boolean)
      return {
        ...r,
        receiptId: r._id || r.id,
        receiptStatus: r.status,
        amount: total || (Number(r.actualAmount ?? r.receivedAmount ?? r.amount ?? 0) || 0),
        vehicleId: r.vehicleId || firstItem.vehicleId,
        plate: formatPlate(r.plate || firstItem.plate || `${r.company || '收款单'}`),
        model: r.model || firstItem.model,
        company: r.company || firstItem.company,
        contactPerson: r.contactPerson || firstItem.contactPerson,
        dueDate: r.dueDate || firstItem.dueDate || lastRangeEnd,
        chargeRangeStart: chargeRangeSource ? (chargeRangeSource.chargeRangeStart || r.chargeRangeStart) : undefined,
        chargeRangeEnd: chargeRangeSource ? (chargeRangeSource.chargeRangeEnd || r.chargeRangeEnd) : undefined,
        rentPeriod: r.rentPeriod || firstItem.rentPeriod,
        rawReceipt: r
      }
    })

  const mapWithVehicle = (list: any[]) =>
    list.map(r => {
      const v = vehicles.find(vh => vh.id === r.vehicleId || vh._id === r.vehicleId || vh.plate === r.plate)
      const reminderDue = r.chargeRangeEnd || r.dueDate || (v ? getVehicleDueDate(v) : undefined)
      return {
        ...r,
        plate: r.plate || v?.plate || '未知车牌',
        model: r.model || v?.type || '',
        company: r.company || v?.company || '',
        contactPerson: r.contactPerson || v?.contactPerson || '',
        amount: r.amount ?? v?.rentAmount ?? 0,
        dueDate: r.dueDate || v?.dueDate,
        reminderDue,
        reminderAmount: v?.reminderAmount ?? v?.rentAmount,
        reminderPeriod: v?.reminderPeriod || v?.rentPeriod,
        reminderPeriodMonths: v?.reminderPeriodMonths,
        voucherData: r.voucherData || r.rawReceipt?.voucherData
      }
    })

  const getPeriodLabel = (value?: string, months?: number) => formatPeriodLabel(value, months)
  const getActualIncomeAmount = (item: any) =>
    Number(
      item?.actualAmount ??
      item?.verifiedAmount ??
      item?.receivedAmount ??
      item?.amount ??
      item?.rawReceipt?.actualAmount ??
      0
    ) || 0
  const getDepositAmount = (receipt: any) => {
    const raw = receipt?.rawReceipt || receipt
    const items = getReceiptItems(raw)
    const fromItems = items.reduce((s: number, it: any) => s + (Number(it?.depositAmount || 0) || 0), 0)
    return Number(raw?.depositAmount ?? fromItems ?? 0) || 0
  }
  const getTradeInAmount = (receipt: any) =>
    Number(receipt?.tradeInAmount ?? receipt?.rawReceipt?.tradeInAmount ?? 0) || 0
  const getDepositOffsetAmount = (receipt: any) =>
    Number(receipt?.depositOffsetAmount ?? receipt?.rawReceipt?.depositOffsetAmount ?? 0) || 0
  const getOtherIncomeAmount = (receipt: any) =>
    Number(receipt?.otherIncomeAmount ?? receipt?.rawReceipt?.otherIncomeAmount ?? 0) || 0
  const buildVoucherLinesByScenario = (receipt: any, company: string): VoucherLine[] => {
    const actual = getActualIncomeAmount(receipt)
    const deposit = getDepositAmount(receipt)
    const tradeIn = getTradeInAmount(receipt)
    const depositOffset = getDepositOffsetAmount(receipt)
    const otherIncome = getOtherIncomeAmount(receipt)
    const receiptType = String(receipt?.receiptType || receipt?.rawReceipt?.receiptType || '')
    const scenario = String(receipt?.accountingScenario || receipt?.rawReceipt?.accountingScenario || 'auto')
    const inferredScenario =
      scenario !== 'auto'
        ? scenario
        : (depositOffset > 0
            ? 'deposit_offset'
            : (tradeIn > 0 && deposit > 0
                ? 'rent_deposit_tradein'
                : (tradeIn > 0
                    ? 'rent_tradein'
                    : (deposit > 0
                        ? (receiptType === 'deposit_only' ? 'deposit_only' : 'rent_deposit')
                        : 'rent_only'))))

    const lines: VoucherLine[] = []
    const addLine = (summary: string, subject: string, debit?: number, credit?: number) => {
      const d = Number(debit || 0)
      const c = Number(credit || 0)
      lines.push({
        summary,
        subject,
        debit: d > 0 ? Number(d.toFixed(2)) : undefined,
        credit: c > 0 ? Number(c.toFixed(2)) : undefined
      })
    }

    if (inferredScenario === 'deposit_only') {
      addLine(`押金-${company}`, '银行存款', actual)
      addLine(`押金-${company}`, '其他应付款-叉车押金', undefined, actual)
      return lines
    }

    if (inferredScenario === 'deposit_offset') {
      const rentRevenue = Math.max(actual + depositOffset + otherIncome, 0)
      addLine(`租金-${company}`, '其他应付款-叉车押金', depositOffset)
      addLine(`租金-${company}`, '银行存款', actual)
      if (otherIncome > 0) addLine(`其他收入-${company}`, '银行存款', otherIncome)
      addLine(`租金-${company}`, '主营业务收入-叉车租赁收入', undefined, rentRevenue)
      return lines
    }

    if (inferredScenario === 'rent_deposit_tradein') {
      const rentRevenue = Math.max(actual - deposit + otherIncome, 0)
      addLine(`收款-${company}`, '银行存款', actual)
      addLine(`旧车折抵-${company}`, '库存商品-旧车', tradeIn)
      if (otherIncome > 0) addLine(`其他收入-${company}`, '银行存款', otherIncome)
      if (rentRevenue > 0) addLine(`租金-${company}`, '主营业务收入-叉车租赁收入', undefined, rentRevenue)
      if (deposit > 0) addLine(`押金-${company}`, '其他应付款-叉车押金', undefined, deposit)
      addLine(`旧车折抵-${company}`, `应收账款-${company}`, undefined, tradeIn)
      return lines
    }

    if (inferredScenario === 'rent_tradein') {
      const rentRevenue = Math.max(actual + tradeIn + otherIncome, 0)
      addLine(`旧车折抵-${company}`, '库存商品-旧车', tradeIn)
      addLine(`收款-${company}`, '银行存款', actual)
      if (otherIncome > 0) addLine(`其他收入-${company}`, '银行存款', otherIncome)
      addLine(`租金-${company}`, '主营业务收入-叉车租赁收入', undefined, rentRevenue)
      return lines
    }

    if (inferredScenario === 'rent_deposit') {
      const rentRevenue = Math.max(actual - deposit + otherIncome, 0)
      addLine(`收款-${company}`, '银行存款', actual)
      if (otherIncome > 0) addLine(`其他收入-${company}`, '银行存款', otherIncome)
      if (rentRevenue > 0) addLine(`租金-${company}`, '主营业务收入-叉车租赁收入', undefined, rentRevenue)
      if (deposit > 0) addLine(`押金-${company}`, '其他应付款-叉车押金', undefined, deposit)
      return lines
    }

    // rent_only
    addLine(`租金-${company}`, '银行存款', actual)
    addLine(`租金-${company}`, '主营业务收入-叉车租赁收入', undefined, Math.max(actual + otherIncome, 0))
    if (otherIncome > 0) addLine(`其他收入-${company}`, '银行存款', otherIncome)
    return lines
  }

  const totalOutstanding = useMemo(
    () => vehicles
      .filter(v => isReceivableVehicleStatus(v.status))
      .reduce((sum, v) => sum + ((v as any)._outstanding ?? 0), 0),
    [vehicles, allReceipts, adjustments]
  )

  useEffect(() => {
    localStorage.setItem('jxc_total_outstanding', totalOutstanding.toFixed(2))
  }, [totalOutstanding])

  const getExpiringVehicles = (limitDays?: number) => {
    const now = dayjs()
    return vehicles
      .filter(v => isReceivableVehicleStatus(v.status))
      .filter(v => getVehicleDueDate(v))
      .filter(v => isLeaseStarted(v, now))
      .filter(v => {
        if (limitDays === undefined) return true
        const diff = dayjs(getVehicleDueDate(v)).diff(now, 'day')
        return diff <= limitDays && diff >= 0
      })
      .sort((a, b) => dayjs(getVehicleDueDate(a)).diff(dayjs(getVehicleDueDate(b))))
  }

  const expiringVehiclesAll = getExpiringVehicles()
  const expiringTop = expiringVehiclesAll.slice(0, 3)

  const rentedVehicles = useMemo(
    () => vehicles.filter(v => isReceivableVehicleStatus(v.status)),
    [vehicles]
  )

  const rentedWithOutstanding = useMemo(
    () => rentedVehicles
      .map(v => ({ vehicle: v, outstanding: (v as any)._outstanding ?? 0 }))
      .sort((a, b) => b.outstanding - a.outstanding),
    [rentedVehicles, allReceipts, adjustments]
  )

  const loadData = async () => {
    try {
      setLoading(true)
      await fetchVehicles().catch(() => {})
      const [pendingRes, completedRes, allRes, adjustmentsRes] = await Promise.all([
        simpleDirectCloudApi.getReceipts('cashier_done'),
        simpleDirectCloudApi.getReceipts('accounted'),
        simpleDirectCloudApi.getReceipts(),
        simpleDirectCloudApi.getVehicleAdjustments()
      ])

      const pendingRaw = pendingRes.success && pendingRes.data ? pendingRes.data : []
      const completedRaw = completedRes.success && completedRes.data ? completedRes.data : []
      const allRaw = allRes.success && allRes.data ? allRes.data : []
      const adjustmentsRaw = adjustmentsRes.success && adjustmentsRes.data ? adjustmentsRes.data : []
      const adjustmentsClean = adjustmentsRaw.filter((adj: any) => adj.type === 'parts' || adj.type === 'repair')

      const pending = mapWithVehicle(normalizeReceipts(pendingRaw))
      const completed = mapWithVehicle(normalizeReceipts(completedRaw))
      setAllReceipts(allRaw)
      setAdjustments(adjustmentsClean)

      const today = new Date().toDateString()
      const completedToday = completedRaw.filter((r: any) => r.accountedAt && new Date(r.accountedAt).toDateString() === today).length
      const adjustmentsDelta = getAdjustmentRevenueDelta(adjustmentsClean)
      const paidIncomeResult = await simpleDirectCloudApi.getTotalPaidReceiptIncome()
      const paidIncome = paidIncomeResult.success && paidIncomeResult.data != null ? paidIncomeResult.data : 0
      const totalAmount = paidIncome + adjustmentsDelta

      setPendingReceipts(pending)
      setCompletedReceipts(completed)
      setStatistics({
        pendingAccounting: pendingRaw.length,
        completedToday,
        totalAmount
      })
    } catch (error) {
      console.error(error)
      message.error('加载数据失败')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出？',
      onOk: () => {
        logout()
        navigate('/login')
      }
    })
  }

  const openVoucherModal = async (receipt: any) => {
    const voucherNo = receipt.voucherNo || `CGZ${Date.now()}`
    const includedItems = pendingReceipts.filter(r => r.receiptId === receipt.receiptId)
    const rawItems = getReceiptItems(receipt.rawReceipt || receipt)
    const detailSource = rawItems.length ? rawItems : includedItems
    const totalAmount = getActualIncomeAmount(receipt)
    const company = receipt.company || includedItems.find(it => it.company)?.company || receipt.customerName || '客户'
    const autoLines: VoucherLine[] = buildVoucherLinesByScenario(receipt, company)
    const lines: VoucherLine[] = receipt.voucherData?.lines?.length
      ? receipt.voucherData.lines
      : autoLines
    const initial: VoucherData = {
      voucherNo,
      date: receipt.voucherData?.date || dayjs().format('YYYY-MM-DD'),
      pageNo: receipt.voucherData?.pageNo || '1',
      totalPages: receipt.voucherData?.totalPages || '1',
      attachmentCount: receipt.voucherData?.attachmentCount || 0,
      accountant: receipt.voucherData?.accountant || user?.nickName || '',
      bookkeeper: receipt.voucherData?.bookkeeper || '',
      maker: receipt.voucherData?.maker || '',
      reviewer: receipt.voucherData?.reviewer || '',
      cashier: receipt.voucherData?.cashier || '',
      printDate: receipt.voucherData?.printDate || dayjs().format('YYYY-MM-DD'),
      lines,
      totalDebit: receipt.voucherData?.totalDebit || lines.reduce((s, l) => s + (Number(l.debit) || 0), 0),
      totalCredit: receipt.voucherData?.totalCredit || lines.reduce((s, l) => s + (Number(l.credit) || 0), 0),
      accountantId: receipt.voucherData?.accountantId || receipt.accountantId || user?.id || '',
      accountantName: receipt.voucherData?.accountantName || receipt.accountantName || user?.nickName || '',
      accountantSealUrl: await resolveAccountantSealUrl(receipt, { allowCurrentUser: true }),
      scope: 'batch',
      items: detailSource.map((it: any) => ({
        plate: formatPlate(it.plate),
        amount: it.actualAmount ?? receipt?.actualAmount ?? it.receivedAmount ?? it.expectedAmount ?? it.amount,
        company: it.company || receipt.company,
        dueDate: it.dueDate,
        periodKey: it.periodKey,
        chargeRangeStart: it.chargeRangeStart,
        chargeRangeEnd: it.chargeRangeEnd
      }))
    }
    setEditingReceipt(receipt)
    voucherForm.setFieldsValue(initial)
    setVoucherModalVisible(true)
  }

  const buildAutoVoucher = (receipt: any): VoucherData => {
    const voucherNo = receipt.voucherNo || `CGZ${Date.now()}`
    const includedItems = pendingReceipts.filter(r => r.receiptId === receipt.receiptId)
    const rawItems = getReceiptItems(receipt.rawReceipt || receipt)
    const detailSource = rawItems.length ? rawItems : includedItems
    const detailActual = detailSource.reduce(
      (s: number, it: any) => s + (Number(it.actualAmount ?? it.receivedAmount ?? it.expectedAmount ?? it.amount ?? 0) || 0),
      0
    )
    const totalAmount = detailActual || getActualIncomeAmount(receipt) || Number(receipt.amount || 0) || 0
    const company = receipt.company || includedItems.find(it => it.company)?.company || receipt.customerName || '客户'
    const lines: VoucherLine[] = buildVoucherLinesByScenario(receipt, company)
    const totalDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0)
    const totalCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0)
    return {
      voucherNo,
      date: dayjs().format('YYYY-MM-DD'),
      pageNo: '1',
      totalPages: '1',
      attachmentCount: 0,
      accountant: user?.nickName || '',
      bookkeeper: '',
      maker: '',
      reviewer: '',
      cashier: '',
      printDate: dayjs().format('YYYY-MM-DD'),
      accountantId: user?.id || '',
      accountantName: user?.nickName || '',
      accountantSealUrl: user?.makerSealUrl || '',
      lines,
      totalDebit,
      totalCredit,
      scope: 'batch',
      items: detailSource.map((it: any) => ({
        plate: formatPlate(it.plate),
        amount: it.actualAmount ?? receipt?.actualAmount ?? it.receivedAmount ?? it.expectedAmount ?? it.amount,
        company: it.company || receipt.company,
        dueDate: it.dueDate,
        periodKey: it.periodKey,
        chargeRangeStart: it.chargeRangeStart,
        chargeRangeEnd: it.chargeRangeEnd
      }))
    }
  }

  const handleAutoAccount = async (receipt: any) => {
    try {
      const voucher = buildAutoVoucher(receipt)
      const raw = receipt?.rawReceipt || {}
      const res = await simpleDirectCloudApi.updateReceipt(receipt.receiptId, {
        status: 'accounted',
        voucherData: voucher,
        voucherNo: voucher.voucherNo,
        accountantId: user?.id,
        accountantName: user?.nickName,
        accountedAt: new Date().toISOString(),
        items: raw.items
      })
      if (res.success) {
        message.success('已自动生成并记账')
        loadData()
      } else {
        message.error(res.error || '自动记账失败')
      }
    } catch (error) {
      console.error(error)
      message.error('自动记账失败')
    }
  }

  const formatChargeRange = (item: any) => {
    if (item?.chargeRangeStart && item?.chargeRangeEnd) {
      return String(item.chargeRangeStart) + '~' + String(item.chargeRangeEnd)
    }
    return item?.periodKey || item?.dueDate || ''
  }
  const handlePreviewVoucher = async () => {
    const values = await voucherForm.validateFields()
    const lines: VoucherLine[] = (values.lines || []).map((l: any) => ({
      summary: l.summary,
      subject: l.subject,
      debit: l.debit ? Number(l.debit) : undefined,
      credit: l.credit ? Number(l.credit) : undefined
    }))
    const items = voucherForm.getFieldValue('items') || []
    const totalDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0)
    const totalCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0)
    const data: VoucherData = {
      ...values,
      accountantId: editingReceipt?.voucherData?.accountantId || editingReceipt?.accountantId || user?.id || '',
      accountantName: editingReceipt?.voucherData?.accountantName || editingReceipt?.accountantName || user?.nickName || '',
      accountantSealUrl: await resolveAccountantSealUrl(editingReceipt, { allowCurrentUser: true }),
      lines,
      totalDebit,
      totalCredit,
      items
    }
    data.accountantSealPreviewUrl = await resolveCloudFileUrl(data.accountantSealUrl)
    setPreviewData(data)
    setVoucherPreviewVisible(true)
  }

  const openAccountedVoucherPreview = async (item: any) => {
    const raw = item?.rawReceipt || item
    let voucher = item?.voucherData || raw?.voucherData
    console.log('[accountant] openAccountedVoucherPreview', { item, raw, voucher })
    if (typeof voucher === 'string') {
      try {
        voucher = JSON.parse(voucher)
      } catch {
        voucher = null
      }
    }
    let lines = voucher?.lines
    if (typeof lines === 'string') {
      try {
        lines = JSON.parse(lines)
      } catch {
        lines = []
      }
    }
    console.log('[accountant] voucher parsed', { voucher, lines })
    if (!voucher || !Array.isArray(lines) || lines.length === 0) {
      message.error('无记账凭证数据')
      return
    }
    const totalDebit = voucher.totalDebit ?? lines.reduce((s: number, l: any) => s + (Number(l.debit) || 0), 0)
    const totalCredit = voucher.totalCredit ?? lines.reduce((s: number, l: any) => s + (Number(l.credit) || 0), 0)
    const receiptItems = Array.isArray(voucher.items) && voucher.items.length
      ? voucher.items
      : getReceiptItems(raw)
    const data: VoucherData = {
      ...voucher,
      voucherNo: voucher.voucherNo || item.voucherNo || raw?.voucherNo || '',
      date: voucher.date || (item.accountedAt ? dayjs(item.accountedAt).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')),
      lines,
      totalDebit,
      totalCredit,
      items: receiptItems
    }
    data.accountantSealUrl = await resolveAccountantSealUrl({
      ...raw,
      ...item,
      ...voucher,
      accountant: voucher?.accountant || item?.accountantName || raw?.accountantName,
      accountantId: item?.accountantId || raw?.accountantId
    }, { allowCurrentUser: false })
    data.accountantSealPreviewUrl = await resolveCloudFileUrl(data.accountantSealUrl)
    setPreviewData(data)
    setVoucherPreviewVisible(true)
  }

  const handleSaveVoucher = async () => {
    try {
      const values = await voucherForm.validateFields()
      const lines: VoucherLine[] = (values.lines || []).map((l: any) => ({
        summary: l.summary,
        subject: l.subject,
        debit: l.debit ? Number(l.debit) : undefined,
        credit: l.credit ? Number(l.credit) : undefined
      }))
      const totalDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0)
      const totalCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0)
      const voucher: VoucherData = {
        ...values,
        accountantId: editingReceipt?.voucherData?.accountantId || editingReceipt?.accountantId || user?.id || '',
        accountantName: editingReceipt?.voucherData?.accountantName || editingReceipt?.accountantName || user?.nickName || '',
        accountantSealUrl: await resolveAccountantSealUrl(editingReceipt, { allowCurrentUser: true }),
        lines,
        totalDebit,
        totalCredit
      }
      const raw = editingReceipt?.rawReceipt || {}
      const res = await simpleDirectCloudApi.updateReceipt(editingReceipt.receiptId, {
        status: 'accounted',
        voucherData: voucher,
        voucherNo: voucher.voucherNo,
        accountantId: user?.id,
        accountantName: user?.nickName,
        accountedAt: new Date().toISOString(),
        items: raw.items
      })
      if (res.success) {
        message.success('凭证保存成功')
        setVoucherModalVisible(false)
        setVoucherPreviewVisible(false)
        setEditingReceipt(null)
        voucherForm.resetFields()
        loadData()
      } else {
        message.error(res.error || '凭证保存失败')
      }
    } catch (error) {
      console.error(error)
      message.error('凭证保存失败')
    }
  }

  const formatMoney = (value: any) => Number(value || 0).toFixed(2)

  const escapeHtml = (value: any) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')

  const downloadExcelHtml = (filename: string, html: string) => {
    const content = `
      <html>
      <head><meta charset="UTF-8" /></head>
      <body>${html}</body>
      </html>
    `
    const blob = new Blob(['\uFEFF' + content], { type: 'application/vnd.ms-excel;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleExportReceivableTable = async () => {
    try {
      const values = await receivableExportForm.validateFields()
      const start = dayjs(values.dateRange?.[0]).startOf('day')
      const end = dayjs(values.dateRange?.[1]).endOf('day')

      const paidStatusSet = new Set(['cashier_done', 'accounted', 'completed'])
      const extractReceiptItems = (receipt: any) => {
        const raw = receipt?.items
        if (Array.isArray(raw)) return raw
        if (raw && typeof raw === 'object') return Object.values(raw)
        if (typeof raw === 'string') {
          try {
            const parsed = JSON.parse(raw)
            if (Array.isArray(parsed)) return parsed
            if (parsed && typeof parsed === 'object') return Object.values(parsed)
          } catch {
            return []
          }
        }
        return []
      }
      const parseDay = (value?: string) => {
        const d = value ? dayjs(value) : null
        return d && d.isValid() ? d.startOf('day') : null
      }

      const rowsRaw = await Promise.all(rentedVehicles
        .map(async (vehicle) => {
          const vehicleId = String(vehicle._id || vehicle.id || vehicle.plate || '')
          const plate = String(vehicle.plate || '')
          const leaseStart = parseDay(vehicle.startDate || '')
          if (!leaseStart || leaseStart.isAfter(end, 'day')) return null

          let lastPaidEnd: dayjs.Dayjs | null = null
          for (const receipt of allReceipts || []) {
            if (!paidStatusSet.has(String(receipt?.status || ''))) continue
            const paidAt = parseDay(receipt?.verifiedAt || receipt?.accountedAt || receipt?.updatedAt || receipt?.createdAt)
            if (paidAt && paidAt.isAfter(end, 'day')) continue
            const items = extractReceiptItems(receipt)
            for (const it of items) {
              const matchVehicle =
                String(it?.vehicleId || '') === vehicleId ||
                String(it?.plate || '') === plate
              if (!matchVehicle) continue
              if (!hasExplicitChargeRange(it, receipt)) continue
              const rangeEnd = parseDay(it?.chargeRangeEnd || receipt?.chargeRangeEnd)
              if (!rangeEnd) continue
              if (!lastPaidEnd || rangeEnd.isAfter(lastPaidEnd, 'day')) {
                lastPaidEnd = rangeEnd
              }
            }
          }

          const receivableStart = (lastPaidEnd ? lastPaidEnd.add(1, 'day') : leaseStart).startOf('day')
          if (receivableStart.isAfter(end, 'day')) return null

          let expected = 0
          try {
            const res = await simpleDirectCloudApi.computeChargeAmount(
              vehicle._id || vehicle.id, receivableStart.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')
            )
            expected = res.success && res.data ? res.data.amount : getExpectedBetween(vehicle, receivableStart.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'))
          } catch {
            expected = getExpectedBetween(vehicle, receivableStart.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'))
          }
          if (expected <= 0) return null

          const periodDays = end.diff(receivableStart, 'day') + 1
          const reason = vehicle.configuration || vehicle.notes || '-'
          return {
            customer: vehicle.company || '-',
            date: receivableStart.format('YYYY/M/D'),
            amount: Number(expected || 0),
            period: `${Math.max(0, periodDays)}天`,
            reason
          }
        }))
      const rows = rowsRaw
        .filter(Boolean)
        .filter((row: any) => {
          const date = dayjs(row.date, 'YYYY/M/D')
          if (!date.isValid()) return true
          return !date.isAfter(end, 'day')
        })
        .filter((row: any) => {
          // 区间过滤用于控制发生时间范围；金额仍按“上次收款截止日 -> 截止日”计算
          const date = dayjs(row.date, 'YYYY/M/D')
          if (!date.isValid()) return true
          return !date.isBefore(start, 'day') && !date.isAfter(end, 'day')
        })
        .map((row: any, idx: number) => ({
          ...row,
          idx: idx + 1
        }))

      const total = rows.reduce((sum, r) => sum + r.amount, 0)
      const bodyRows = rows.map((r) => `
        <tr>
          <td style="border:1px solid #000;padding:6px;text-align:center;">${r.idx}</td>
          <td style="border:1px solid #000;padding:6px;">${escapeHtml(r.customer)}</td>
          <td style="border:1px solid #000;padding:6px;">${escapeHtml(r.date)}</td>
          <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(formatMoney(r.amount))}</td>
          <td style="border:1px solid #000;padding:6px;text-align:center;">${escapeHtml(r.period)}</td>
          <td style="border:1px solid #000;padding:6px;">${escapeHtml(r.reason)}</td>
        </tr>
      `).join('')

      const html = `
        <div style="font-family:'Microsoft YaHei',SimSun,serif;">
          <div style="font-size:18px;font-weight:700;margin-bottom:8px;">应收表</div>
          <div style="margin-bottom:10px;">统计区间：${escapeHtml(start.format('YYYY-MM-DD'))} 至 ${escapeHtml(end.format('YYYY-MM-DD'))}</div>
          <table style="border-collapse:collapse;width:100%;">
            <thead>
              <tr style="background:#f5f5f5;font-weight:700;">
                <th style="border:1px solid #000;padding:6px;">序号</th>
                <th style="border:1px solid #000;padding:6px;">客户名称</th>
                <th style="border:1px solid #000;padding:6px;">发生日期</th>
                <th style="border:1px solid #000;padding:6px;">应收金额</th>
                <th style="border:1px solid #000;padding:6px;">账期</th>
                <th style="border:1px solid #000;padding:6px;">备注（未收原因）</th>
              </tr>
            </thead>
            <tbody>
              ${bodyRows}
              <tr style="font-weight:700;background:#f5f5f5;">
                <td style="border:1px solid #000;padding:6px;text-align:center;">合计</td>
                <td style="border:1px solid #000;padding:6px;">—</td>
                <td style="border:1px solid #000;padding:6px;">—</td>
                <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(formatMoney(total))}</td>
                <td style="border:1px solid #000;padding:6px;">—</td>
                <td style="border:1px solid #000;padding:6px;">—</td>
              </tr>
            </tbody>
          </table>
        </div>
      `
      downloadExcelHtml(`应收表_${start.format('YYYYMMDD')}_${end.format('YYYYMMDD')}.xls`, html)
      message.success('应收表已导出')
      setReceivableExportVisible(false)
    } catch (err: any) {
      if (err?.errorFields) return
      message.error(err?.message || '导出失败')
    }
  }

  const handleExportReceivedTable = async () => {
    try {
      const values = await receivedExportForm.validateFields()
      const start = dayjs(values.dateRange?.[0]).startOf('day')
      const end = dayjs(values.dateRange?.[1]).endOf('day')
      const paidStatusSet = new Set(['cashier_done', 'accounted', 'completed'])

      const vehicleByPlate = new Map(vehicles.map(v => [String(v.plate || ''), v]))
      const vehicleById = new Map(vehicles.map(v => [String(v._id || v.id || ''), v]))

      const rows = (allReceipts || [])
        .filter((r: any) => paidStatusSet.has(String(r.status || '')))
        .filter((r: any) => {
          const t = dayjs(r.verifiedAt || r.accountedAt || r.updatedAt || r.createdAt)
          return t.isValid() && !t.isBefore(start) && !t.isAfter(end)
        })
        .sort((a: any, b: any) => dayjs(a.verifiedAt || a.accountedAt || a.updatedAt || a.createdAt).valueOf() - dayjs(b.verifiedAt || b.accountedAt || b.updatedAt || b.createdAt).valueOf())
        .map((r: any, idx: number) => {
          const receivedAt = dayjs(r.verifiedAt || r.accountedAt || r.updatedAt || r.createdAt)
          const amount = Number(r.actualAmount ?? r.verifiedAmount ?? r.amount ?? 0)
          const vehicle =
            vehicleById.get(String(r.vehicleId || '')) ||
            vehicleByPlate.get(String(r.plate || ''))
          const remark = vehicle?.configuration || vehicle?.notes || '-'
          const method = r.paymentMethod || r.rawReceipt?.paymentMethod || (Array.isArray(r.items) ? r.items[0]?.paymentMethod : '') || '-'
          return {
            idx: idx + 1,
            customer: r.company || r.customerName || vehicle?.company || '-',
            date: receivedAt.isValid() ? receivedAt.format('YYYY/M/D') : '-',
            amount,
            method,
            remark
          }
        })

      const total = rows.reduce((sum, r) => sum + r.amount, 0)
      const bodyRows = rows.map((r) => `
        <tr>
          <td style="border:1px solid #000;padding:6px;text-align:center;">${r.idx}</td>
          <td style="border:1px solid #000;padding:6px;">${escapeHtml(r.customer)}</td>
          <td style="border:1px solid #000;padding:6px;">${escapeHtml(r.date)}</td>
          <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(formatMoney(r.amount))}</td>
          <td style="border:1px solid #000;padding:6px;">${escapeHtml(r.method)}</td>
          <td style="border:1px solid #000;padding:6px;">${escapeHtml(r.remark)}</td>
        </tr>
      `).join('')

      const html = `
        <div style="font-family:'Microsoft YaHei',SimSun,serif;">
          <div style="font-size:18px;font-weight:700;margin-bottom:8px;">已收表</div>
          <div style="margin-bottom:10px;">统计区间：${escapeHtml(start.format('YYYY-MM-DD'))} 至 ${escapeHtml(end.format('YYYY-MM-DD'))}</div>
          <table style="border-collapse:collapse;width:100%;">
            <thead>
              <tr style="background:#f5f5f5;font-weight:700;">
                <th style="border:1px solid #000;padding:6px;">序号</th>
                <th style="border:1px solid #000;padding:6px;">客户名称</th>
                <th style="border:1px solid #000;padding:6px;">收款日期</th>
                <th style="border:1px solid #000;padding:6px;">收款金额</th>
                <th style="border:1px solid #000;padding:6px;">收款方式</th>
                <th style="border:1px solid #000;padding:6px;">备注</th>
              </tr>
            </thead>
            <tbody>
              ${bodyRows}
              <tr style="font-weight:700;background:#f5f5f5;">
                <td style="border:1px solid #000;padding:6px;text-align:center;">合计</td>
                <td style="border:1px solid #000;padding:6px;">—</td>
                <td style="border:1px solid #000;padding:6px;">—</td>
                <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(formatMoney(total))}</td>
                <td style="border:1px solid #000;padding:6px;">—</td>
                <td style="border:1px solid #000;padding:6px;">—</td>
              </tr>
            </tbody>
          </table>
        </div>
      `
      downloadExcelHtml(`已收表_${start.format('YYYYMMDD')}_${end.format('YYYYMMDD')}.xls`, html)
      message.success('已收表已导出')
      setReceivedExportVisible(false)
    } catch (err: any) {
      if (err?.errorFields) return
      message.error(err?.message || '导出失败')
    }
  }

  const handleExportSummaryTable = async () => {
    try {
      const values = await summaryExportForm.validateFields()
      const periodType = values.periodType as 'month' | 'year'
      const start = dayjs(values.dateRange?.[0]).startOf('day')
      const end = dayjs(values.dateRange?.[1]).endOf('day')
      const paidStatusSet = new Set(['cashier_done', 'accounted', 'completed'])
      const extractReceiptItems = (receipt: any) => {
        const raw = receipt?.items
        if (Array.isArray(raw)) return raw
        if (raw && typeof raw === 'object') return Object.values(raw)
        if (typeof raw === 'string') {
          try {
            const parsed = JSON.parse(raw)
            if (Array.isArray(parsed)) return parsed
            if (parsed && typeof parsed === 'object') return Object.values(parsed)
          } catch {
            return []
          }
        }
        return []
      }

      const paidInRangeByVehicle = new Map<string, number>()
      const paidToEndByVehicle = new Map<string, number>()
      const paidToBeforeStartByVehicle = new Map<string, number>()

      const addPaid = (map: Map<string, number>, key: string, amount: number) => {
        if (!key || !Number.isFinite(amount) || amount <= 0) return
        map.set(key, (map.get(key) || 0) + amount)
      }

      const getReceiptRentEntries = (receipt: any) => {
        const items = extractReceiptItems(receipt)
        const sourceItems = items.length ? items : [receipt]
        return sourceItems
          .map((it: any) => {
            const vehicleId = String(it?.vehicleId || receipt?.vehicleId || '')
            const plate = String(it?.plate || receipt?.plate || '')
            const key = vehicleId || plate
            const receiptType = String(it?.receiptType || receipt?.receiptType || 'rent')
            const rentAmount = receiptType === 'deposit_only'
              ? 0
              : (Number(it?.receivedAmount ?? it?.verifiedAmount ?? it?.expectedAmount ?? it?.amount ?? 0) || 0)
            return { key, rentAmount }
          })
          .filter((e: any) => Boolean(e.key) && e.rentAmount > 0)
      }

      for (const receipt of allReceipts || []) {
        if (!paidStatusSet.has(String(receipt?.status || ''))) continue
        const paidAt = dayjs(receipt?.verifiedAt || receipt?.accountedAt || receipt?.updatedAt || receipt?.createdAt)
        if (!paidAt.isValid() || paidAt.isAfter(end)) continue
        const entries = getReceiptRentEntries(receipt)

        if (!entries.length) continue

        entries.forEach((e: any, idx: number) => {
          const allocated = Math.max(0, Number(e.rentAmount || 0))
          if (allocated <= 0) return
          addPaid(paidToEndByVehicle, e.key, allocated)
          if (paidAt.isBefore(start)) {
            addPaid(paidToBeforeStartByVehicle, e.key, allocated)
          } else {
            addPaid(paidInRangeByVehicle, e.key, allocated)
          }
        })
      }

      const activeVehicles = vehicles.filter(v => String(v.status || '') === 'rented')
      // Batch fetch outstanding at start/end dates from backend
      const endDateStr = end.format('YYYY-MM-DD')
      const startDateStr = start.subtract(1, 'day').endOf('day').format('YYYY-MM-DD')
      const [endBatchRes, startBatchRes] = await Promise.all([
        simpleDirectCloudApi.batchOutstandingAtDate(endDateStr),
        simpleDirectCloudApi.batchOutstandingAtDate(startDateStr)
      ])
      const endBatch = endBatchRes.success && endBatchRes.data ? endBatchRes.data : {}
      const startBatch = startBatchRes.success && startBatchRes.data ? startBatchRes.data : {}

      const rows = activeVehicles.map((vehicle) => {
        const keyId = String(vehicle._id || vehicle.id || '')
        const keyPlate = String(vehicle.plate || '')
        const leaseStart = dayjs(getActiveLeaseStart(vehicle) || vehicle.startDate)
        const received = Number((paidInRangeByVehicle.get(keyId) || 0) + (paidInRangeByVehicle.get(keyPlate) || 0))
        const endingOutstanding = endBatch[keyId]?.outstanding ?? (vehicle as any)._outstanding ?? 0
        const openingOutstanding = startBatch[keyId]?.outstanding ?? 0
        const periodExpected = Math.max(endingOutstanding - openingOutstanding + received, 0)
        return {
          customer: vehicle.company || '-',
          forkliftNo: vehicle.plate || '-',
          orderDate: leaseStart.isValid() ? leaseStart.format('YYYY/M/D') : '-',
          productName: vehicle.type || '-',
          expected: endingOutstanding,
          received,
          openingOutstanding,
          periodExpected
        }
      })

      const endingBalance = rows.reduce((sum: number, r) => sum + r.expected, 0)
      const actualReceived = rows.reduce((sum: number, r) => sum + r.received, 0)
      const openingBalance = rows.reduce((sum: number, r) => sum + r.openingOutstanding, 0)
      const newExpected = rows.reduce((sum: number, r) => sum + r.periodExpected, 0)

      const detailRows = rows.map((r, idx) => `
        <tr>
          <td style="border:1px solid #000;padding:6px;text-align:center;">${idx + 1}</td>
          <td style="border:1px solid #000;padding:6px;">${escapeHtml(r.customer)}</td>
          <td style="border:1px solid #000;padding:6px;">${escapeHtml(r.forkliftNo)}</td>
          <td style="border:1px solid #000;padding:6px;">${escapeHtml(r.orderDate)}</td>
          <td style="border:1px solid #000;padding:6px;">${escapeHtml(r.productName)}</td>
          <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(formatMoney(r.expected))}</td>
          <td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(formatMoney(r.received))}</td>
        </tr>
      `).join('')

      const periodLabel = periodType === 'year' ? '年汇总' : '月汇总'
      const html = `
        <div style="font-family:'Microsoft YaHei',SimSun,serif;">
          <div style="font-size:18px;font-weight:700;margin-bottom:8px;">汇总表（${periodLabel}）</div>
          <div style="margin-bottom:10px;">统计区间：${escapeHtml(start.format('YYYY-MM-DD'))} 至 ${escapeHtml(end.format('YYYY-MM-DD'))}</div>
          <table style="border-collapse:collapse;width:100%;">
            <thead>
              <tr style="background:#f5f5f5;font-weight:700;">
                <th style="border:1px solid #000;padding:6px;">序号</th>
                <th style="border:1px solid #000;padding:6px;">客户名称</th>
                <th style="border:1px solid #000;padding:6px;">叉车编号</th>
                <th style="border:1px solid #000;padding:6px;">订单日期</th>
                <th style="border:1px solid #000;padding:6px;">车辆型号</th>
                <th style="border:1px solid #000;padding:6px;">应收金额</th>
                <th style="border:1px solid #000;padding:6px;">已收金额</th>
              </tr>
            </thead>
            <tbody>
              ${detailRows}
            </tbody>
          </table>
          <table style="border-collapse:collapse;width:60%;margin-top:14px;">
            <thead>
              <tr style="background:#f5f5f5;font-weight:700;">
                <th style="border:1px solid #000;padding:6px;">项目</th>
                <th style="border:1px solid #000;padding:6px;">金额（元）</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style="border:1px solid #000;padding:6px;">期初应收账款余额</td><td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(formatMoney(openingBalance))}</td></tr>
              <tr><td style="border:1px solid #000;padding:6px;">本期新增应收账款</td><td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(formatMoney(newExpected))}</td></tr>
              <tr><td style="border:1px solid #000;padding:6px;">本期实际已收款</td><td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(formatMoney(actualReceived))}</td></tr>
              <tr><td style="border:1px solid #000;padding:6px;">期末应收账款余额</td><td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(formatMoney(endingBalance))}</td></tr>
              <tr style="font-weight:700;background:#f5f5f5;"><td style="border:1px solid #000;padding:6px;">汇总应收</td><td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(formatMoney(newExpected))}</td></tr>
              <tr style="font-weight:700;background:#f5f5f5;"><td style="border:1px solid #000;padding:6px;">汇总已收</td><td style="border:1px solid #000;padding:6px;text-align:right;">${escapeHtml(formatMoney(actualReceived))}</td></tr>
            </tbody>
          </table>
        </div>
      `
      downloadExcelHtml(`汇总表_${start.format('YYYYMMDD')}_${end.format('YYYYMMDD')}.xls`, html)
      message.success('汇总表已导出')
      setSummaryExportVisible(false)
    } catch (err: any) {
      if (err?.errorFields) return
      message.error(err?.message || '导出失败')
    }
  }

  const renderReceiptPreview = (items: VoucherItem[], receipt?: any) => {
    if (!items.length) return null
    const rawReceipt = receipt?.rawReceipt || receipt
    const headerItem = items[0]
    const receiptNo = rawReceipt?.receiptId || rawReceipt?._id || rawReceipt?.id || '-'
    const company = rawReceipt?.company || headerItem?.company || rawReceipt?.customerName || '-'
    const date = rawReceipt?.verifiedAt || rawReceipt?.createdAt || rawReceipt?.accountedAt
    const rangeLabel = formatChargeRange(headerItem) || rawReceipt?.periodKey || rawReceipt?.dueDate || ''
    const rentTotal = items.reduce((sum, it) => sum + (Number(it.amount ?? 0) || 0), 0)
    const depositTotal =
      (Number(rawReceipt?.depositAmount) || 0) +
      (Array.isArray(rawReceipt?.items)
        ? rawReceipt.items.reduce((s: number, it: any) => s + (Number(it?.depositAmount || 0) || 0), 0)
        : 0)
    const total = rentTotal + depositTotal
    const receiptType = rawReceipt?.receiptType
    const titleText = receiptType === 'deposit_only' ? '押金收据' : '收款收据'
    const hasRentLines = receiptType !== 'deposit_only' && rentTotal > 0

    return (
      <div style={{ border: '1px solid #e5e5e5', borderRadius: 8, padding: 12, marginBottom: 16 }}>
        <div style={{ textAlign: 'center', fontSize: 20, fontWeight: 700, letterSpacing: 4 }}>
          {titleText}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <div>客户名称: {company}</div>
          <div>No: {receiptNo}</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <div>收款范围: {rangeLabel || '-'}</div>
          <div>日期: {date ? dayjs(date).format('YYYY-MM-DD') : '-'}</div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10, fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #333', padding: 6, width: '10%' }}>货号</th>
              <th style={{ border: '1px solid #333', padding: 6 }}>品名及规格</th>
              <th style={{ border: '1px solid #333', padding: 6, width: '8%' }}>单位</th>
              <th style={{ border: '1px solid #333', padding: 6, width: '8%' }}>数量</th>
              <th style={{ border: '1px solid #333', padding: 6, width: '12%' }}>单价</th>
              <th style={{ border: '1px solid #333', padding: 6, width: '12%' }}>金额</th>
            </tr>
          </thead>
          <tbody>
            {hasRentLines && items.map((it, idx) => {
              const amount = Number(it.amount ?? 0) || 0
              const plate = it.plate || rawReceipt?.plate || '-'
              return (
                <tr key={idx}>
                  <td style={{ border: '1px solid #333', padding: 6 }} />
                  <td style={{ border: '1px solid #333', padding: 6 }}>车辆租金（{plate}）</td>
                  <td style={{ border: '1px solid #333', padding: 6 }}>台</td>
                  <td style={{ border: '1px solid #333', padding: 6 }}>1</td>
                  <td style={{ border: '1px solid #333', padding: 6, textAlign: 'right' }}>{formatMoney(amount)}</td>
                  <td style={{ border: '1px solid #333', padding: 6, textAlign: 'right' }}>{formatMoney(amount)}</td>
                </tr>
              )
            })}
            {depositTotal > 0 && (
              <tr>
                <td style={{ border: '1px solid #333', padding: 6 }} />
                <td style={{ border: '1px solid #333', padding: 6 }}>押金</td>
                <td style={{ border: '1px solid #333', padding: 6 }}>-</td>
                <td style={{ border: '1px solid #333', padding: 6 }}>1</td>
                <td style={{ border: '1px solid #333', padding: 6, textAlign: 'right' }}>{formatMoney(depositTotal)}</td>
                <td style={{ border: '1px solid #333', padding: 6, textAlign: 'right' }}>{formatMoney(depositTotal)}</td>
              </tr>
            )}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <div>合计人民币: {formatMoney(total)}</div>
          <div>￥{formatMoney(total)}</div>
        </div>
      </div>
    )
  }
  const VoucherPreview: React.FC<{ data: VoucherData }> = ({ data }) => {
    const totalDebit = data.totalDebit ?? data.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0)
    const totalCredit = data.totalCredit ?? data.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0)
    const formatCNDate = (value?: string) => {
      const d = value ? dayjs(value) : dayjs()
      return d.isValid() ? d.format('YYYY 年 MM 月 DD 日') : ''
    }
    const pageNo = data.pageNo || '1'
    const totalPages = data.totalPages || '1'

    return (
      <div style={{ background: '#fff', color: '#000', padding: 16, fontFamily: '"SimSun","STSong","Noto Serif SC",serif' }}>
        <div style={{ textAlign: 'center', fontSize: 26, fontWeight: 700, letterSpacing: 6, marginBottom: 8 }}>
          记账凭证
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
          <span>{formatCNDate(data.date)}</span>
          <span>凭证号:{data.voucherNo}</span>
          <span>{pageNo}/{totalPages}</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 6 }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #000', padding: 6, width: '20%' }}>摘要</th>
              <th style={{ border: '1px solid #000', padding: 6, width: '40%' }}>科目名称</th>
              <th style={{ border: '1px solid #000', padding: 6, width: '20%' }}>借方金额</th>
              <th style={{ border: '1px solid #000', padding: 6, width: '20%' }}>贷方金额</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: 6 }}>{line.summary}</td>
                <td style={{ border: '1px solid #000', padding: 6 }}>{line.subject}</td>
                <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>
                  {line.debit ? formatMoney(line.debit) : ''}
                </td>
                <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>
                  {line.credit ? formatMoney(line.credit) : ''}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ border: '1px solid #000', padding: 6 }}>合计</td>
              <td style={{ border: '1px solid #000', padding: 6 }} />
              <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>{formatMoney(totalDebit)}</td>
              <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>{formatMoney(totalCredit)}</td>
            </tr>
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 12 }}>
          <span>附件:{data.attachmentCount || 0} 张</span>
          <span>打印日期:{data.printDate || ''}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12 }}>
          <span>会计主管:{data.bookkeeper || ''}</span>
          <span>记账:{data.accountant || ''}</span>
          <span>出纳:{data.cashier || ''}</span>
          <span>审核:{data.reviewer || ''}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 6, fontSize: 12 }}>
          <span>制证:{data.maker || ''}</span>
        </div>
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 4, fontSize: 12 }}>会计签名</div>
            {data.accountantSealPreviewUrl ? (
              <img
                src={data.accountantSealPreviewUrl}
                alt="accountant seal"
                style={{ width: 220, height: 120, border: '1px solid #ddd', borderRadius: 4, objectFit: 'contain' }}
              />
            ) : (
              <div style={{ width: 220, height: 120, border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                未上传会计签名
              </div>
            )}
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
            打印
          </Button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', background: 'var(--bg)', minHeight: '100vh' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="large">
              <Avatar size={64} style={{ backgroundColor: 'var(--positive)' }}>ACC</Avatar>
              <div>
                <Title level={4} style={{ margin: 0 }}>{user?.nickname || user?.nickName || user?.username || 'accountant'}</Title>
                <Text type="secondary">{user?.info || '会计'}</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button onClick={() => {
                receivableExportForm.setFieldsValue({ dateRange: [dayjs().startOf('month'), dayjs()] })
                setReceivableExportVisible(true)
              }}>
                导出应收表
              </Button>
              <Button onClick={() => {
                receivedExportForm.setFieldsValue({ dateRange: [dayjs().startOf('month'), dayjs()] })
                setReceivedExportVisible(true)
              }}>
                导出已收表
              </Button>
              <Button onClick={() => {
                summaryExportForm.setFieldsValue({
                  periodType: 'month',
                  dateRange: [dayjs().startOf('month'), dayjs()]
                })
                setSummaryExportVisible(true)
              }}>
                导出汇总表
              </Button>
              <Button icon={<LogoutOutlined />} onClick={handleLogout}>退出</Button>
            </Space>
          </Col>
        </Row>
      </Card>

          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="待记账收款" value={statistics.pendingAccounting} valueStyle={{ color: 'var(--accent)' }} prefix={<CalculatorOutlined />} />
              </Card>
            </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="今日记账" value={statistics.completedToday} valueStyle={{ color: 'var(--positive)' }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="累计收款" value={statistics.totalAmount} valueStyle={{ color: 'var(--warning)' }} prefix={<DollarOutlined />} formatter={(v) => `￥${Number(v || 0).toFixed(2)}`} />
          </Card>
        </Col>
      </Row>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Space>
              <Text strong>在租/承包车辆应收</Text>
              <Tag color={rentedWithOutstanding.length ? 'orange' : 'default'}>
                {rentedWithOutstanding.length} 台
              </Tag>
              <Text type="secondary">合计</Text>
              <Tag
                color="gold"
                style={{ fontSize: 16, fontWeight: 600, padding: '2px 10px' }}
              >
                ￥{totalOutstanding.toFixed(2)}
              </Tag>
            </Space>
          </Col>
          <Col>
            <Button type="link" onClick={() => setRentedExpanded(prev => !prev)}>
              {rentedExpanded ? '收起' : '查看全部'}
            </Button>
          </Col>
        </Row>
        {rentedExpanded && (
          <List
            itemLayout="vertical"
            dataSource={rentedWithOutstanding}
            renderItem={({ vehicle, outstanding }) => {
              const dueDate = getVehicleDueDate(vehicle)
              return (
                <List.Item key={vehicle._id || vehicle.id || vehicle.plate}>
                  <List.Item.Meta
                    avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                    title={(
                      <Space>
                        <Text strong>{vehicle.plate}</Text>
                        {vehicle.status === 'contracted' && <Tag color="cyan">承包车</Tag>}
                        <Tag color={outstanding > 0 ? 'red' : 'green'}>待收 ￥{outstanding.toFixed(2)}</Tag>
                        <Tag color="orange">{dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : '-'}</Tag>
                      </Space>
                    )}
                    description={(
                      <div>
                        <Text type="secondary">公司：</Text>
                        <Text>{vehicle.company || '-'}</Text>
                        <Text type="secondary" style={{ marginLeft: 12 }}>联系人：</Text>
                        <Text>{vehicle.contactPerson || '-'}</Text>
                        <Text type="secondary" style={{ marginLeft: 12 }}>租金：</Text>
                        <Text>￥{vehicle.rentAmount || 0}</Text>
                      </div>
                    )}
                  />
                </List.Item>
              )
            }}
          />
        )}
      </Card>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'pending',
              label: (
                <Badge count={pendingReceipts.length} size="small">
                  <span>待记账收款</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={pendingReceipts}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button key="auto-account" icon={<CheckCircleOutlined />} onClick={() => handleAutoAccount(item)}>
                          一键记账
                        </Button>,
                        <Button key="voucher" type="primary" icon={<FileTextOutlined />} onClick={() => openVoucherModal(item)}>
                          记账
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar style={{ backgroundColor: 'var(--accent)' }}>{item.plate}</Avatar>}
                        title={
                          <Space>
                            <Text strong>{item.plate}</Text>
                            <Tag color="blue">待记账</Tag>
                          </Space>
                        }
                        description={
                          <div>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">车型</Text> <Text>{item.model}</Text></Col>
                              <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">金额</Text> <Text strong style={{ color: 'var(--positive)' }}>￥{getActualIncomeAmount(item).toFixed(2)}</Text></Col>
                              <Col span={12}><Text type="secondary">到期日</Text> <Text>{item.reminderDue || item.dueDate || '-'}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">提醒周期</Text> <Tag>{getPeriodLabel(item.reminderPeriod, item.reminderPeriodMonths)}</Tag></Col>
                              <Col span={12}><Text type="secondary">提醒金额</Text> <Text>￥{Number(item.reminderAmount ?? item.amount ?? 0).toFixed(2)}</Text></Col>
                            </Row>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              )
            },
            {
              key: 'completed',
              label: (
                <Badge count={completedReceipts.length} size="small">
                  <span>已记账收款</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={completedReceipts}
                  renderItem={(item) => (
                    <List.Item actions={[
                        <Button key="accounted-receipt" icon={<FileTextOutlined />} onClick={() => openAccountedVoucherPreview(item)}>查看记账</Button>
                      ]}>
                      <List.Item.Meta
                        avatar={<Avatar style={{ backgroundColor: 'var(--positive)' }}>{item.plate}</Avatar>}
                        title={
                          <Space>
                            <Text strong>{item.plate}</Text>
                            <Tag color="green">已记账</Tag>
                          </Space>
                        }
                        description={
                          <div>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company}</Text></Col>
                              <Col span={12}><Text type="secondary">周期</Text> <Tag>{item.periodKey || '周期'}</Tag></Col>
                            </Row>
                            <Text type="secondary">金额</Text> <Text strong>￥{getActualIncomeAmount(item).toFixed(2)}</Text><br />
                            <Text type="secondary">记账时间</Text> <Text>{item.accountedAt ? dayjs(item.accountedAt).format('YYYY-MM-DD HH:mm') : '-'}</Text>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              )
            }
          ]}
        />
      </Card>

      <Modal
        title="记账凭证"
        open={voucherModalVisible}
        onCancel={() => { setVoucherModalVisible(false); setVoucherPreviewVisible(false); setEditingReceipt(null); voucherForm.resetFields() }}
        footer={[
          <Button key="preview" icon={<EyeOutlined />} onClick={handlePreviewVoucher}>预览</Button>,
          <Button key="ok" type="primary" icon={<CheckCircleOutlined />} onClick={handleSaveVoucher}>保存</Button>
        ]}
        width={900}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        {renderReceiptPreview(watchedItems || [], editingReceipt)}
        <Form form={voucherForm} layout="vertical">
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item label="凭证号" name="voucherNo" rules={[{ required: true, message: '必填' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="日期" name="date" rules={[{ required: true, message: '必填' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="页码" name="pageNo">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="总页数" name="totalPages">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="items" hidden>
            <Input />
          </Form.Item>

          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                <div style={{ background: '#fafafa', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                  <Row gutter={8} style={{ fontSize: 12, fontWeight: 600 }}>
                    <Col span={7}>摘要</Col>
                    <Col span={7}>科目</Col>
                    <Col span={4}>借方金额</Col>
                    <Col span={4}>贷方金额</Col>
                    <Col span={2} />
                  </Row>
                </div>
                {fields.map((field) => (
                  <Row gutter={8} key={field.key} align="middle">
                    <Col span={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'summary']}
                        rules={[{ required: true, message: '必填' }]}
                      >
                        <Input placeholder="摘要" />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'subject']}
                        rules={[{ required: true, message: '必填' }]}
                      >
                        <Input placeholder="科目" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item {...field} name={[field.name, 'debit']}>
                        <InputNumber style={{ width: '100%' }} placeholder="借方金额" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item {...field} name={[field.name, 'credit']}>
                        <InputNumber style={{ width: '100%' }} placeholder="贷方金额" />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Button type="text" icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()}>
                  添加行
                </Button>
              </>
            )}
          </Form.List>

          <Row gutter={12} style={{ marginTop: 12 }}>
            <Col span={6}>
              <Form.Item label="附件数量" name="attachmentCount">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="会计主管" name="bookkeeper">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="记账" name="accountant">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="出纳" name="cashier">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item label="审核" name="reviewer">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="制证" name="maker">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="打印日期" name="printDate">
                <Input placeholder="2024-01-01" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        open={voucherPreviewVisible}
        footer={null}
        onCancel={() => setVoucherPreviewVisible(false)}
        width={960}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      >
        {previewData && <VoucherPreview data={previewData} />}
      </Modal>

      <Modal
        title="到期提醒"
        open={expiringModalVisible}
        onCancel={() => setExpiringModalVisible(false)}
        footer={null}
        width={720}
      >
        <List
          itemLayout="vertical"
          dataSource={expiringVehiclesAll}
          renderItem={(vehicle) => {
            const dueDate = getVehicleDueDate(vehicle)
            return (
              <List.Item key={vehicle._id || vehicle.id || vehicle.plate}>
                <List.Item.Meta
                  avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                  title={
                    <Space>
                      <Text strong>{vehicle.plate}</Text>
                      <Tag color="orange">{dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : '-'}</Tag>
                      <Tag color="gold">租金 ¥{Number(vehicle.reminderAmount ?? vehicle.rentAmount ?? 0).toFixed(2)}</Tag>
                    </Space>
                  }
                  description={
                    <div>
                      <Text type="secondary">公司：</Text>
                      <Text>{vehicle.company || '-'}</Text>
                      <Text type="secondary" style={{ marginLeft: 12 }}>联系人：</Text>
                      <Text>{vehicle.contactPerson || '-'}</Text>
                    </div>
                  }
                />
              </List.Item>
            )
          }}
        />
      </Modal>

      <Modal
        title="导出应收表"
        open={receivableExportVisible}
        onCancel={() => setReceivableExportVisible(false)}
        onOk={handleExportReceivableTable}
        okText="导出"
      >
        <Form form={receivableExportForm} layout="vertical">
          <Form.Item name="dateRange" label="统计日期范围" rules={[{ required: true, message: '请选择日期范围' }]}>
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="导出已收表"
        open={receivedExportVisible}
        onCancel={() => setReceivedExportVisible(false)}
        onOk={handleExportReceivedTable}
        okText="导出"
      >
        <Form form={receivedExportForm} layout="vertical">
          <Form.Item name="dateRange" label="统计日期范围" rules={[{ required: true, message: '请选择日期范围' }]}>
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="导出汇总表"
        open={summaryExportVisible}
        onCancel={() => setSummaryExportVisible(false)}
        onOk={handleExportSummaryTable}
        okText="导出"
      >
        <Form form={summaryExportForm} layout="vertical">
          <Form.Item name="periodType" label="汇总区间类型" rules={[{ required: true, message: '请选择区间类型' }]}>
            <Select
              options={[
                { label: '月', value: 'month' },
                { label: '年', value: 'year' }
              ]}
            />
          </Form.Item>
          <Form.Item name="dateRange" label="开始到截止日期" rules={[{ required: true, message: '请选择日期范围' }]}>
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default AccountantPage
















